import { PrintDeliverySheetButton } from "@/components/orders/print-delivery-sheet-button";
// routes/_main/orders/index.tsx
import { OrdersStream } from "@/components/tables/orders-stream"; // NEW
import { OrdersExportMenu } from "@/components/tables/orders";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { buildMetadata } from "@/lib/metadata";
import { useAuth } from "@/providers/auth";
import { OrderService } from "@/services/order-services";
import type { Order, Paginated } from "@/services/types/orders";
import { Link, createFileRoute } from "@tanstack/react-router";
import { UploadCloud } from "lucide-react";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/_main/orders/")({
  component: RouteComponent,
  loader: async () => {
    // ✅ Fetch only FIRST page for immediate display
    const initialPage = await OrderService.getOrdersPage({ pageSize: 10 });
    return { initialPage };
  },
  head: () => ({
    meta: buildMetadata({
      title: "Channel Genie",
      description: "View all orders",
      keywords: ["orders", "order", "view"],
    }),
  }),
});

function RouteComponent() {
  const { initialPage } = Route.useLoaderData() as {
    initialPage: Paginated<Order>;
  };
  const [loadedOrders, setLoadedOrders] = useState<Order[]>(
    () => initialPage.results,
  );
  const [filteredOrders, setFilteredOrders] = useState<Order[]>(
    () => initialPage.results,
  );
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const { user } = useAuth();
  const isWarehouseUser = user?.supplierRole === "warehouse_user";
  const hasOrders = loadedOrders.length > 0;
  const displayName = user?.display?.trim() || user?.username || "there";

  // Background async loading of remaining pages
  useEffect(() => {
    let isMounted = true;
    let currentPage = 2;

    const loadNextPages = async () => {
      if (!initialPage.next) return;

      while (isMounted && initialPage.next) {
        const nextPageData = await OrderService.getOrdersPage({
          page: currentPage,
          pageSize: 10,
        });

        if (!isMounted) break;

        setLoadedOrders((prev) => {
          // Avoid duplicates when merging
          const newOrders = nextPageData.results.filter(
            (order) => !prev.some((item) => item.id === order.id),
          );
          return newOrders.length > 0 ? [...prev, ...newOrders] : prev;
        });

        if (!nextPageData.next) break;
        currentPage += 1;
      }
    };

    loadNextPages();

    return () => {
      isMounted = false;
    };
  }, [initialPage.next]);

  return (
    <div className="space-y-8 p-4 md:p-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <h1 className="font-semibold text-3xl tracking-tight md:hidden">
            Hello, {displayName}
          </h1>
          <p className="text-muted-foreground text-sm">
            Here's an overview of your orders.
          </p>
        </div>
        <div className="flex w-full flex-col gap-3 self-start sm:w-auto sm:flex-row">
          <div className="grid w-full grid-cols-2 items-center gap-3 sm:flex sm:w-auto sm:gap-2">
            {selectedOrderIds.length > 0 && (
              <Badge
                variant="secondary"
                className="col-span-2 w-fit rounded-full px-2.5 py-1 font-medium text-[10px] uppercase tracking-wide sm:col-auto"
              >
                Selected: {selectedOrderIds.length}
              </Badge>
            )}
            <PrintDeliverySheetButton
              hasOrders={hasOrders}
              orders={loadedOrders}
              selectedOrderIds={selectedOrderIds}
            />
            <OrdersExportMenu
              orders={filteredOrders}
              buttonClassName="hidden rounded-xl sm:inline-flex"
            />
            {!isWarehouseUser && (
              <Button
                asChild
                size="lg"
                className="w-full rounded-xl bg-foreground text-background hover:bg-foreground/90 sm:w-auto"
              >
                <Link to="/orders/upload">
                  <UploadCloud className="size-4" />
                  Upload Orders
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ✅ This component renders page 1 immediately and streams the rest */}
      <OrdersStream
        all_orders={loadedOrders}
        onOrdersChange={setLoadedOrders}
        onFilteredOrdersChange={setFilteredOrders}
        onSelectedOrderIdsChange={setSelectedOrderIds}
      />
    </div>
  );
}
